import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, toInputDateUK, apiError } from "@/lib/api";
import DocumentUpload from "@/components/DocumentUpload";
import ConfirmDetails from "@/components/ConfirmDetails";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Save, Send, AlertTriangle, Loader2 } from "lucide-react";

const CATEGORIES = [
  "Rent", "Electricity and utilities", "Salaries and wages", "Repairs and maintenance",
  "Cleaning", "Transport", "Fuel", "Office supplies", "Bank charges", "Miscellaneous",
];

export default function EditExpensePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(2);
  useEffect(() => { document.querySelector("[data-testid=main-scroll]")?.scrollTo(0, 0); }, [step]);
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [noReceipt, setNoReceipt] = useState(false);
  const [exceptionReason, setExceptionReason] = useState("");
  const [exceptionStatus, setExceptionStatus] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const needsException = documents.length === 0 && noReceipt && exceptionStatus !== "approved";
  const [storeId, setStoreId] = useState(null);

  const [form, setForm] = useState({
    category: "", supplier_payee: "", description: "", amount: "", vat_amount: "",
    date: "", payment_method: "", notes: "",
  });

  useEffect(() => {
    let live = true;
    api.get(`/expenses/${id}`).then((res) => {
      if (!live) return;
      const e = res.data;
      if (!["draft", "rejected"].includes(e.status)) { navigate(`/view-expense/${id}`, { replace: true }); return; }
      setStoreId(e.store_id);
      setNoReceipt(e.manager_exception || false);
      setExceptionReason(e.exception_reason || "");
      setExceptionStatus(e.exception_status || null);
      setForm({
        category: e.category || "", supplier_payee: e.supplier_payee || "",
        description: e.description || "", amount: e.amount || "",
        vat_amount: e.vat_amount || "", date: toInputDateUK(e.date),
        payment_method: e.payment_method || "", notes: e.notes || "",
      });
      if (e.document_ids?.length) {
        setDocuments(e.document_ids.map((did) => {
          const docInfo = e.documents?.find((d) => d.id === did);
          return { id: did, original_filename: docInfo?.original_filename || `Document ${did}` };
        }));
      }
      setLoading(false);
    }).catch(() => {
      toast.error("Could not load expense");
      navigate("/submissions");
    });
  }, [id, navigate]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (submit = false) => {
    if (submit && !form.category) return toast.error("Choose a category before submitting. You can save a draft without one.");
    if (needsException && exceptionReason.trim().length < 10) return toast.error("Explain why there is no receipt (at least 10 characters)");
    setSaving(true);
    try {
      const payload = {
        category: form.category || null, supplier_payee: form.supplier_payee,
        description: form.description, amount: parseFloat(form.amount) || 0,
        vat_amount: parseFloat(form.vat_amount) || 0, date: form.date,
        payment_method: form.payment_method || null,
        document_ids: documents.map((d) => d.id),
        manager_exception: noReceipt, exception_reason: noReceipt ? exceptionReason : null, notes: form.notes,
      };
      await api.put(`/expenses/${id}`, payload);
      if (submit && !needsException) {
        await api.patch(`/expenses/${id}/submit`, { confirmed });
        toast.success("Expense submitted for approval");
      } else {
        toast.success(needsException ? "Draft saved — waiting for a manager to approve the no-receipt exception" : "Draft updated");
      }
      navigate("/submissions", { state: { tab: "expenses" } });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const steps = ["Upload Receipt", "Expense Details", "Review & Submit"];

  return (
    <div className="space-y-5" data-testid="edit-expense-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/submissions")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40 hover:text-navy" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-heading font-bold text-navy">Edit Expense</h1>
      </div>

      <StepIndicator steps={steps} current={step} />

      {step === 1 && (
        <div className="space-y-4">
          {documents.length > 0 && (
            <div className="bg-white rounded-xl border border-navy/10 p-3">
              <p className="text-sm text-navy font-medium">{documents.length} document(s) attached</p>
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2">
                  <p className="text-xs text-navy/50 truncate">{d.original_filename || `Doc #${d.id}`}</p>
                  <button type="button" onClick={() => setDocuments((p) => p.filter((x) => x.id !== d.id))} className="min-h-[44px] px-3 text-xs text-red-500" data-testid={`detach-document-${d.id}-btn`}>Remove</button>
                </div>
              ))}
            </div>
          )}
          {!noReceipt && storeId && (
            <DocumentUpload kind="receipt" storeId={storeId} onUpload={(doc) => setDocuments((p) => [...p, doc])} onRemove={(did) => setDocuments((p) => p.filter((x) => x.id !== did))} />
          )}
          <label className="flex items-center justify-between min-h-[56px] py-2 px-3 bg-white rounded-lg border border-navy/10">
            <div><p className="text-sm text-navy font-medium">No receipt available</p><p className="text-xs text-navy/40">Requires manager exception{exceptionStatus ? ` — ${exceptionStatus}` : ""}</p></div>
            <Switch checked={noReceipt} onCheckedChange={setNoReceipt} className="scale-125" data-testid="no-receipt-switch" />
          </label>
          {noReceipt && (
            <Textarea value={exceptionReason} onChange={(e) => setExceptionReason(e.target.value)} rows={2} className="gm-input"
              placeholder="Why is there no receipt? (required, at least 10 characters)" data-testid="exception-reason-input" />
          )}
          <button onClick={() => setStep(2)} data-testid="step1-next-btn" className="w-full gm-btn-gold flex items-center justify-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="gm-input" data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Payee / Supplier"><Input value={form.supplier_payee} onChange={(e) => set("supplier_payee", e.target.value)} className="gm-input" data-testid="expense-payee-input" /></Field>
          <Field label="Description"><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="gm-input" /></Field>
          <div className="grid grid-cols-3 gap-2.5">
            <Field label="Amount (£)"><Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="gm-input font-mono font-bold" data-testid="expense-amount-input" /></Field>
            <Field label="VAT (£)"><Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => set("vat_amount", e.target.value)} className="gm-input font-mono" /></Field>
            <Field label="Date"><Input value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="DD/MM/YYYY" className="gm-input" data-testid="expense-date-input" /></Field>
          </div>
          <Field label="Payment Method">
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger className="gm-input"><SelectValue placeholder="Select method" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="card">Company Card</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Separator />
          <div className="flex gap-3">
            <button onClick={() => setStep(1)} data-testid="step2-back-btn" className="flex-1 gm-btn-navy flex items-center justify-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => setStep(3)} data-testid="step2-next-btn" className="flex-1 gm-btn-gold flex items-center justify-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="gm-input" /></Field>
          <Card className="border-navy/10 shadow-none"><CardContent className="p-4">
            <h3 className="font-semibold text-navy text-sm mb-2">Summary</h3>
            <div className="grid grid-cols-2 gap-y-1.5 text-sm">
              <span className="text-navy/50">Category</span><span className="text-navy font-medium">{form.category || "\u2014"}</span>
              <span className="text-navy/50">Amount</span><span className="font-mono font-bold text-navy">{formatGBP(form.amount)}</span>
              <span className="text-navy/50">Documents</span><span>{documents.length > 0 ? `${documents.length} attached` : noReceipt ? "Manager exception" : "None"}</span>
            </div>
          </CardContent></Card>
          <Separator />
          <div className="flex gap-3"><button onClick={() => setStep(2)} data-testid="step3-back-btn" className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back</button></div>
          <div className="flex flex-col gap-2 sticky bottom-0 z-10 bg-cream -mx-4 px-4 pt-3 pb-3 border-t border-navy/10" data-testid="sticky-actions">
            {!needsException && <ConfirmDetails checked={confirmed} onChange={setConfirmed} kind="receipt" />}
            <div className="flex gap-3">
            <button onClick={() => save(false)} disabled={saving} className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm" data-testid="save-draft-btn"><Save className="w-4 h-4" /> Update</button>
            <button onClick={() => save(true)} disabled={saving || (documents.length === 0 && !noReceipt) || (!needsException && !confirmed)} className="flex-1 gm-btn-gold flex items-center justify-center gap-2 text-sm" data-testid="submit-submission-button"><Send className="w-4 h-4" /> {needsException ? "Save & request exception" : "Submit"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-sm font-medium text-navy">{label}</Label>{children}</div>);
}
function StepIndicator({ steps, current }) {
  return (<div className="flex items-center gap-1.5">{steps.map((s, i) => (<div key={s} className="flex items-center gap-1.5 flex-1"><div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i+1<current?"gm-step-completed":i+1===current?"gm-step-active":"gm-step-pending"}`}>{i+1<current?<Check className="w-3.5 h-3.5"/>:i+1}</div><span className={`text-[11px] hidden sm:inline ${i+1===current?"text-navy font-medium":"text-navy/30"}`}>{s}</span>{i<steps.length-1&&<div className="flex-1 h-px bg-navy/10"/>}</div>))}</div>);
}
